import {
  createTheme,
  PaletteColor,
  PaletteColorOptions,
  PaletteMode,
  Theme,
  ThemeOptions,
} from '@mui/material';
import { grey } from '@mui/material/colors';

import interFontUrl from './fonts/Inter-VariableFont_opsz,wght.ttf';

declare module '@mui/material/styles' {
  interface TypeText {
    // The design needs extra colors for texts that are not part of the
    // MUI theme. These colors are used for better contrast in e.g the
    // Account cards.
    primaryAlternativeContrast: string;
    sectionBody: string;
  }

  interface BreakpointOverrides {
    sm: true;
  }

  interface TypeBackground {
    avatar: string;
  }

  interface PaletteOptions {
    darkDivider: string;
    neutral: PaletteColorOptions;
  }

  interface Palette {
    darkDivider: string;
    neutral: PaletteColor;
  }

  interface ThemeOptions {
    // Would be nice to have this property in the Shape interface
    // but that does not seem to be possible.
    borderRadiusLarge?: number;
  }

  interface Theme {
    borderRadiusLarge: number;
  }

  interface TypographyVariants {
    subtitle3: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    subtitle3?: React.CSSProperties;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    subtitle3: true;
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    neutral: true;
  }
}

declare module '@mui/material/ButtonGroup' {
  interface ButtonGroupPropsColorOverrides {
    neutral: true;
  }
}

/**
 * Builds the mode-specific slice of the palette. Custom tokens
 * (`neutral`, `darkDivider`, `background.avatar`, `text.primaryAlternativeContrast`,
 * `text.sectionBody`) are not provided by MUI so both modes need explicit
 * values. Everything else (text.primary/secondary, background.default/paper,
 * divider, action.*) is left to MUI's own light/dark defaults, which already
 * match the design (e.g. dark background.default `#121212` / paper `#1e1e1e`).
 *
 * Colors are derived from the PDN-4737 prototype recording; exact design
 * tokens have not been shared yet, so these may need tuning once they are.
 */
const createModePalette = (mode: PaletteMode): ThemeOptions['palette'] => {
  // Not passing `mode` here: this is only used to read MUI's default `info`
  // palette values, which are the same regardless of light/dark mode.
  const defaultTheme = createTheme();

  if (mode === 'dark') {
    return {
      mode,
      primary: {
        main: '#FFFFFF',
      },
      text: {
        primaryAlternativeContrast: 'rgba(0, 0, 0, 0.6)',
        sectionBody: '#B3B3B3',
      },
      darkDivider: 'rgba(255, 255, 255, 0.16)',
      background: {
        avatar: 'rgba(255, 255, 255, 0.08)',
      },
      neutral: {
        light: 'rgba(255, 255, 255, 0.06)',
        main: 'rgba(255, 255, 255, 0.16)',
        dark: grey[400],
        contrastText: 'rgba(255, 255, 255, 0.87)',
      },
      info: {
        ...defaultTheme.palette.info,
        light: 'rgba(41, 121, 255, 0.18)',
      },
      success: {
        light: 'rgba(111, 207, 118, 0.18)',
        main: '#6FCF76',
        dark: '#8FE39A',
        contrastText: '#0B2E0E',
      },
      warning: {
        light: 'rgba(224, 196, 104, 0.2)',
        main: '#E0C468',
        dark: '#F5E7A8',
        contrastText: '#241D02',
      },
      error: {
        light: 'rgba(241, 120, 120, 0.18)',
        main: '#F17878',
        dark: '#FFB4B4',
        contrastText: '#2B0A0A',
      },
    };
  }

  return {
    mode,
    primary: {
      main: '#000000',
    },
    text: {
      primaryAlternativeContrast: 'rgba(255, 255, 255, 0.6)',
      sectionBody: '#9C9C9C',
      secondary: 'rgba(0, 0, 0, 0.6)',
    },
    darkDivider: 'rgba(0, 0, 0, 0.16)',
    background: {
      avatar: 'rgba(0, 0, 0, 0.06)',
    },
    neutral: {
      light: grey.A100,
      main: grey.A200,
      dark: grey.A700,
      contrastText: 'rgba(0, 0, 0, 0.87)',
    },
    info: {
      ...defaultTheme.palette.info,
      light: '#E6F3FA',
    },
    success: {
      light: '#E8F5E9',
      main: '#56B95E',
      dark: '#217F28',
      contrastText: '#FFF',
    },
    warning: {
      light: '#FAEFC1',
      main: '#F0DD8F',
      dark: '#A98900',
      contrastText: '#FFFFFF',
    },
    error: {
      light: '#FFD6D6',
      main: '#F99D9D',
      dark: '#B03838',
      contrastText: '#211F1F',
    },
  };
};

export const createMerchantTheme = (mode: PaletteMode = 'light'): Theme => {
  const baseTheme = createTheme({
    typography: {
      fontFamily: 'Inter, sans-serif',
    },
    palette: createModePalette(mode),
    shape: {
      borderRadius: 6,
    },
    borderRadiusLarge: 16,
    breakpoints: {
      values: {
        xs: 0,
        sm: 480,
        md: 900,
        lg: 1200,
        xl: 1536,
      },
    },
  });

  return createTheme(baseTheme, {
    components: {
      MuiAlert: {
        styleOverrides: {
          standardInfo: {
            backgroundColor: baseTheme.palette.info.light,
            color: baseTheme.palette.info.dark,

            '& .MuiAlert-icon': {
              color: baseTheme.palette.info.dark,
            },
          },
          standardError: {
            backgroundColor: baseTheme.palette.error.light,
            color: baseTheme.palette.error.dark,

            '& .MuiAlert-icon': {
              color: baseTheme.palette.error.dark,
            },
          },
          standardSuccess: {
            backgroundColor: baseTheme.palette.success.light,
            color: baseTheme.palette.success.dark,

            '& .MuiAlert-icon': {
              color: baseTheme.palette.success.dark,
            },
          },
          standardWarning: {
            backgroundColor: baseTheme.palette.warning.light,
            color: baseTheme.palette.warning.dark,

            '& .MuiAlert-icon': {
              color: baseTheme.palette.warning.dark,
            },
          },
        },
      },
      MuiList: {
        styleOverrides: {
          root: {
            gap: '20px',
          },
        },
      },
      MuiListItem: {
        defaultProps: {
          disableGutters: true,
        },
        styleOverrides: {
          root: {
            minHeight: '54px',
            borderRadius: `${baseTheme.shape.borderRadius}px`,

            '& .MuiSkeleton-root': {
              width: '100%',
              height: '54px',
              borderRadius: `${baseTheme.shape.borderRadius}px`,
            },
          },
        },
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: {
            minWidth: '48px',
            paddingRight: baseTheme.spacing(2),
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            paddingTop: baseTheme.spacing(1),
            paddingBottom: baseTheme.spacing(1),
          },
        },
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            display: 'inline-flex',
            justifyContent: 'center',
            alignItems: 'start',
            paddingTop: baseTheme.spacing(1),
          },
        },
        variants: [
          {
            props: { loading: true },
            style: {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            },
          },
          {
            props: { size: 'large' },
            style: {
              height: '48px',
              paddingTop: '12px',
            },
          },
          {
            props: { variant: 'text' },
            style: {
              textTransform: 'none',
            },
          },
          {
            props: { variant: 'outlined', color: 'neutral' },
            style: {
              background: baseTheme.palette.background.paper,
              borderColor: baseTheme.palette.divider,
              color: baseTheme.palette.text.secondary,

              '&:hover': {
                background: baseTheme.palette.action.hover,
              },
            },
          },
        ],
      },
      MuiTypography: {
        defaultProps: {
          variantMapping: {
            subtitle3: 'p',
          },
        },
        variants: [
          {
            props: { variant: 'h1' },
            style: { lineHeight: '140%', fontSize: '48px' },
          },
          {
            props: { variant: 'h2' },
            style: { lineHeight: '140%', fontSize: '34px' },
          },
          {
            props: { variant: 'h3' },
            style: { lineHeight: '140%', fontSize: '24px' },
          },
          {
            props: { variant: 'h4' },
            style: { lineHeight: '140%', fontSize: '20px' },
          },
          {
            props: { variant: 'h5' },
            style: { lineHeight: '140%' },
          },
          {
            props: { variant: 'h6' },
            style: { lineHeight: '140%' },
          },
          {
            props: { variant: 'subtitle1' },
            style: { lineHeight: '140%', fontWeight: 500 /* medium */ },
          },
          {
            props: { variant: 'subtitle2' },
            style: { lineHeight: '140%' },
          },
          {
            props: { variant: 'subtitle3' },
            style: {
              fontSize: '10px',
              lineHeight: '140%',
            },
          },
          {
            props: { variant: 'body1' },
            style: { lineHeight: '140%' },
          },
          {
            props: { variant: 'body2' },
            style: { lineHeight: '140%' },
          },
        ],
      },
      MuiCard: {
        defaultProps: {
          elevation: 1,
        },
        styleOverrides: {
          root: {
            backgroundColor: baseTheme.palette.background.paper,
            border: `1px solid ${baseTheme.palette.divider}`,
            borderRadius: `${baseTheme.shape.borderRadius}px`,
          },
        },
      },
      MuiCardHeader: {
        styleOverrides: {
          title: {
            color: baseTheme.palette.primary.contrastText,
          },
          subheader: {
            color: baseTheme.palette.text.primaryAlternativeContrast,
          },
        },
      },
      MuiCircularProgress: {
        variants: [
          {
            props: { size: 'large' },
            style: { width: '32px', height: '32px' },
          },
        ],
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: 'none',
            borderBottom: 'none',
            color: baseTheme.palette.text.primary,
            backgroundColor: baseTheme.palette.background.paper,
          },
        },
      },
      MuiAvatar: {
        styleOverrides: {
          root: {
            color: baseTheme.palette.text.secondary,
            backgroundColor: baseTheme.palette.background.avatar,
            paddingTop: '3px',
          },
        },
      },
      MuiBottomNavigationAction: {
        styleOverrides: {
          root: {
            paddingTop: baseTheme.spacing(2),
            paddingBottom: baseTheme.spacing(3),
          },
          label: {
            background: 'transparent',

            '&.Mui-selected': {
              // Use default font size even though selected. Otherwise
              // we get the text clipping over the pin icon.
              fontSize: '0.75rem',
            },
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          '@font-face': {
            fontFamily: 'Inter',
            fontStyle: 'normal',
            fontDisplay: 'swap',
            src: `url(${interFontUrl}) format('truetype')`,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 16,
            overflow: 'hidden',
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            backgroundColor: baseTheme.palette.neutral.light,
            paddingTop: baseTheme.spacing(3),
            paddingBottom: baseTheme.spacing(2.5),
            paddingLeft: baseTheme.spacing(3),
            paddingRight: baseTheme.spacing(3),
          },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            backgroundColor: baseTheme.palette.neutral.light,
            paddingTop: baseTheme.spacing(2.5),
            paddingBottom: baseTheme.spacing(3),
            paddingLeft: baseTheme.spacing(3),
            paddingRight: baseTheme.spacing(3),
          },
        },
      },
    },
  } satisfies ThemeOptions);
};
